import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatPriceAUD, formatDateAU } from '../../../shared/format';
import type { PortfolioHolding } from '../../../shared/api';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';

/**
 * Holding price cell. Shows the native price (and AUD) for foreign lines, marks
 * hand- or import-priced lines (managed funds, SMAs) with their date, flags
 * unpriced ones, and lets financial.edit users set a price inline.
 */
export function HoldingPriceCell({ h }: { h: PortfolioHolding }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canSet = can(user?.role, 'financial.edit');
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(h.currentPrice > 0 ? String(h.currentPrice) : '');
  const [asOf, setAsOf] = useState(new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane' }).format(new Date()));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const unpriced = h.currentPrice <= 0;

  async function save() {
    const n = Number(price.replace(/[$,\s]/g, ''));
    if (!Number.isFinite(n) || n <= 0) { setErr('Enter a price above zero'); return; }
    setBusy(true); setErr(null);
    try {
      await api(`/api/securities/${encodeURIComponent(h.code)}/price`, { method: 'PATCH', body: JSON.stringify({ price: n, asOf }) });
      await qc.invalidateQueries();
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally { setBusy(false); }
  }

  if (editing) {
    return (
      <div className="flex flex-col items-end gap-1 min-w-[9rem]" data-testid={`price-editor-${h.code}`}>
        <input className="qa-input qa-mono text-xs w-28 text-right" inputMode="decimal" aria-label={`Price for ${h.code}`}
          value={price} onChange={(e) => setPrice(e.target.value)} autoFocus />
        <input type="date" className="qa-input text-xs w-36" aria-label={`Price date for ${h.code}`} value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        <div className="flex gap-2 text-[11px]">
          <button type="button" className="text-ink-mute hover:text-ink" onClick={() => setEditing(false)}>Cancel</button>
          <button type="button" className="text-rust font-semibold" disabled={busy} onClick={save}>Save price</button>
        </div>
        {err && <span role="alert" className="text-[10px] text-rust">{err}</span>}
      </div>
    );
  }

  const main = h.currency && h.nativePrice !== undefined ? (
    <span title={h.fxMissing
      ? `No ${h.currency}/AUD rate available: shown unconverted`
      : `${h.currency} ${h.nativePrice.toFixed(2)} at ${h.fxRate?.toFixed(4)} = A$${h.currentPrice.toFixed(2)}`}>
      <span className="block">{h.currency} {h.nativePrice.toFixed(2)}</span>
      <span className={`block text-[10px] ${h.fxMissing ? 'text-rust' : 'text-ink-mute'}`}>
        {h.fxMissing ? 'no FX rate' : formatPriceAUD(h.currentPrice)}
      </span>
    </span>
  ) : unpriced ? <span className="text-rust font-semibold">No price</span> : formatPriceAUD(h.currentPrice, { unitPrice: h.manualPriced });

  return (
    <span className="inline-flex flex-col items-end">
      {main}
      {(h.manualPriced || unpriced) && (
        <span className="text-[10px] text-ink-mute whitespace-nowrap">
          {h.priceSource === 'import' ? 'from report' : h.priceSource === 'manual' ? 'set by hand' : ''}
          {h.priceAsOf && !unpriced ? ` ${formatDateAU(h.priceAsOf)}` : ''}
          {canSet && (
            <button type="button" className="ml-1 text-rust underline underline-offset-2" onClick={() => setEditing(true)}>
              {unpriced ? 'Set price' : 'Update'}
            </button>
          )}
        </span>
      )}
    </span>
  );
}
